import { useAtom, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { Button, Empty, Group, Text } from '@ekhodealer/ekho-common/rev';

import type { ConversationListRow } from '@ekhodealer/ekho-common';

import { useResolveConversation } from '@common/api/conversations/useResolveConversation';
import { ConversationList } from '@common/components/conversations/ConversationList';
import { ConversationStatusTag } from '@common/components/conversations/ConversationStatusTag';
import { ConversationView } from '@common/components/conversations/ConversationView';
import NoLeads from '@common/components/icons/NoLeads';
import { formatChannelLabels } from '@common/lib/strings';

import { selectedConversationAtom } from '../atoms/conversationAtoms';
import { useConversationNavigation } from '../hooks/useConversationNavigation';

import { ConversationReplyComposer } from './ConversationReplyComposer';
import { ConversationsContactPanel } from './ConversationsContactPanel';

import './ConversationsLayout.scss';

const CheckmarkIcon = (): React.ReactNode => (
	<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
		<path
			d='M13.5 4.5L6 12L2.5 8.5'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

interface ResolveButtonProps {
	conversationId: string;
	leadId: string;
	isResolved: boolean;
}

const ResolveButton = ({ conversationId, leadId, isResolved }: ResolveButtonProps): React.ReactNode => {
	const { mutate, isPending } = useResolveConversation();
	const setSelectedConversation = useSetAtom(selectedConversationAtom);

	if (isResolved) {
		return (
			<div className='conversations-resolve-button'>
				<Button variant='filled' size='medium' isDisabled icon={<CheckmarkIcon />}>
					Resolved
				</Button>
			</div>
		);
	}

	const handleResolve = (): void => {
		mutate({ conversationId, leadId }, {
			onSuccess: (data) => {
				setSelectedConversation((prev) =>
					prev?.conversationId === data.conversationId
						? { ...prev, status: data.status, dateResolved: data.dateResolved }
						: prev,
				);
			},
		});
	};

	return (
		<Button variant='filled' size='small' isLoading={isPending} onPress={handleResolve}>
			Mark as resolved
		</Button>
	);
};

export const ConversationsLayout = (): React.ReactNode => {
	const [selectedConversation, setSelectedConversation] = useAtom(selectedConversationAtom);
	const { conversationId: urlConversationId, openConversation } = useConversationNavigation();

	const handleSelect = useCallback(
		(conversation: ConversationListRow): void => {
			setSelectedConversation(conversation);
			// Only navigate if the URL doesn't already point at this conversation
			if (conversation.conversationId !== urlConversationId) {
				openConversation(conversation.conversationId);
			}
		},
		[setSelectedConversation, openConversation, urlConversationId],
	);

	return (
		<div className='conversations-layout'>
			<div className='conversations-layout__left'>
				<ConversationList
					selectedId={urlConversationId ?? selectedConversation?.conversationId ?? null}
					onSelect={handleSelect}
					autoSelectId={urlConversationId}
					showEscalatedSection
				/>
			</div>
			<div className='conversations-layout__center'>
				{selectedConversation ? (
					<div className='conversations-message-thread'>
						<div className='conversations-message-thread-header'>
							<Group
								itemsSpacing='12'
								itemsAlignX='space-between'
								itemsAlignY='center'
								padding={{ x: 24, y: 12 }}>
								<Group itemsSpacing='12' itemsAlignY='center'>
									<Group.Item>
										<Text size='body' weight='semibold'>
											{selectedConversation.contactName || 'Anonymous Buyer'}
										</Text>
									</Group.Item>
									<Group.Item>
										<Text size='bodySmall' color='secondary'>
											{formatChannelLabels(selectedConversation.channels)}
										</Text>
									</Group.Item>
									<Group.Item>
										<ConversationStatusTag
											status={selectedConversation.status}
										/>
									</Group.Item>
								</Group>
								{(selectedConversation.status === 'escalated' ||
									selectedConversation.dateEscalated ||
									selectedConversation.dateResolved) && (
									<Group.Item>
										<ResolveButton
											conversationId={selectedConversation.conversationId}
											leadId={selectedConversation.leadId}
											isResolved={selectedConversation.dateResolved != null}
										/>
									</Group.Item>
								)}
							</Group>
						</div>
						<div className='conversations-message-thread__messages'>
							<ConversationView
								leadId={selectedConversation.leadId}
								conversationId={selectedConversation.conversationId}
							/>
						</div>
						<ConversationReplyComposer />
					</div>
				) : (
					<div className='conversations-message-thread conversations-message-thread--empty'>
						<Empty
							icon={<NoLeads />}
							title='No conversation selected'
							description='Select a conversation from the list to view the message thread.'
						/>
					</div>
				)}
			</div>
			{selectedConversation && (
				<div className='conversations-layout__right'>
					<ConversationsContactPanel leadId={selectedConversation.leadId} />
				</div>
			)}
		</div>
	);
};
